import type { ReactChild, ReactNode, ReactText } from 'react';
import type {
  PressableProps,
  PressableStateCallbackType as UIEventState,
  TextStyle,
  ViewStyle,
} from 'react-native';

import type { IconName } from '../Icon';

export type ButtonProps = GarnishProps &
  PressableProps &
  ButtonChildren &
  FocusOutlineProps &
  IconProp & {
    readonly width?: number | string;
    readonly style?: ViewStyle;
    readonly rightElement?: (uiEventState: UIEventState) => ReactNode;
  };

export type GarnishStyleProps = {
  readonly hasDisabledStyle?: boolean;
};

export type GarnishProps = {
  readonly size?: SizeTypes;
  readonly palette?: PaletteTypes;
  readonly isLoading?: boolean;
} & GarnishStyleProps;

export type ButtonChildren = {
  readonly children?: ReactChild | ReactText;
};

export type BoxProps = {
  readonly children: ReactNode;
  readonly uiEventState: UIEventState;
  readonly style?: ViewStyle;
} & Required<GarnishProps>;

export type ButtonIconProps = {
  readonly uiEventState: UIEventState;
} & Required<GarnishProps> &
  IconProp;

export type TextParentProps = {
  readonly uiEventState: UIEventState;
} & Required<GarnishProps> &
  ButtonChildren;

export type LoadingDotsProps = {
  readonly uiEventState: UIEventState;
} & Required<GarnishProps>;

type IconProp = {
  readonly centerIcon?: IconName;
  readonly leftIcon?: IconName;
  readonly rightIcon?: IconName;
};

export type FocusOutlineProps = {
  readonly focusOutlineType?: 'ring' | 'rectangular';
  readonly focusOutlineColor?: ViewStyle['borderColor'];
};

export type SizeTypes = Required<keyof typeof ButtonSize>;
export type PaletteTypes = Required<keyof typeof ButtonPalette>;

export const ButtonSize = {
  xsmall: 'xsmall',
  small: 'small',
  medium: 'medium',
  large: 'large',
  xlarge: 'xlarge',
};

export const ButtonPalette = {
  primary: 'primary',
  primaryOutline: 'primaryOutline',
  secondary: 'secondary',
  secondaryWithCautionHover: 'secondaryWithCautionHover',
  inverted: 'inverted',
  caution: 'caution',
  lime: 'lime',
  quinoa: 'quinoa',
  light: 'light',
  muted: 'muted',
  outlinedLight: 'outlinedLight',
};

export type ButtonStyleBlock = {
  readonly box: ViewStyle;
  readonly text: TextStyle;
};
